import { Device, Tag } from "cortex/ui/src/fleet/view/device/model";

import { sortByLabelAsc } from "./sortByProp";

type DeviceMap = Record<string, Device>;

type TargetOption = {
  isDisabled: boolean;
  label: string;
  value: string;
};

function getTargetDevice(device: Device, isDisabled = false): TargetOption {
  const { id, name, device_id: deviceId } = device;

  return {
    isDisabled,
    label: `device: ${name} (${deviceId})`,
    value: `d_${id}`,
  };
}

function getTargetTag(tag: Tag, isDisabled = false): TargetOption {
  const { id, name } = tag;

  return {
    isDisabled,
    label: `tag: ${name}`,
    value: `t_${id}`,
  };
}

function _extractTargets(
  devices: DeviceMap = {},
  isDisabled: boolean,
  includeTags = true,
): Record<string, TargetOption> {
  return Object.values(devices).reduce<Record<string, TargetOption>>(
    (acc, device) => {
      const { tags = [] } = device;
      const target = getTargetDevice(device, isDisabled);
      acc[target.value] = target;

      if (includeTags) {
        tags.forEach((tag) => {
          const target = getTargetTag(tag, isDisabled);
          acc[target.value] = target;
        });
      }

      return acc;
    },
    {},
  );
}

/**
 * Builds an array of select options for device targets.
 *
 * @param {Object} devices - a device byId object.
 * @param {Object} deletedDevices - a deleted device byId object.
 * @return {Object[]} - Returns an array of select options.
 */
export function getTargetOptions(
  devices: DeviceMap = {},
  deletedDevices: DeviceMap = {},
): TargetOption[] {
  const deviceTargets = _extractTargets(devices, false);
  const deletedDeviceTargets = _extractTargets(deletedDevices, true, false);

  const targets = {
    ...deviceTargets,
    ...deletedDeviceTargets,
  };

  return Object.values(targets)
    .map((target) => {
      const { isDisabled, label, value } = target;

      return {
        value,
        label,
        isDisabled,
      };
    })
    .sort(sortByLabelAsc);
}
